import React, { useEffect, useState, useCallback } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import { useStore } from 'zustand';
import { useUnityBuildStore } from '../../stores/store';
import {
  useCreateChangeOrderMutation,
  useUpdateChangeOrderMutation,
} from '../../hooks/useCreateChangeOrderMutation';
import { ChangeOrder } from '../../api/protosCompiled/changeOrder/changeOrder_pb';
import { useFetchChangeOrders } from '../../hooks/useFetchChangeOrders';
import {
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import { useFetchEstimateStatuses, useFetchEstimates } from '../../hooks/useFetchProjectEstimates';
import { EstimateStatus } from '../../api/protosCompiled/projectEstimate/projectEstimate_pb';

// Helper function to get brief status descriptions for MenuItems
const getStatusMenuDescription = (statusName: string): string => {
  const statusLower = statusName.toLowerCase();

  switch (statusLower) {
    case 'draft':
      return 'Still developing';
    case 'pending':
      return 'Ready for review';
    case 'rejected':
      return 'Client declined';
    case 'declined':
      return 'Not pursuing';
    case 'void':
      return 'No longer valid';
    case 'accepted':
      return 'Won/approved';
    default:
      return '';
  }
};

// Helper function to format date to "yyyy-MM-dd"
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toISOString().split('T')[0]; // Extract only the date part
};

// Default template for change order description
const getDefaultChangeOrderDescriptionTemplate = (): string => {
  return `
    <p><strong>Provide&nbsp;a&nbsp;60KW&nbsp;electrical&nbsp;generator&nbsp;...&nbsp;123&nbsp;I&nbsp;this&nbsp;thing&nbsp;on&nbsp;</strong></p>
    <p class="ql-indent-1">One&nbsp;widget&nbsp;1</p>
    <p class="ql-indent-1">Two&nbsp;Widget&nbsp;2</p>
    <p></p>
    <p></p>
    <p class="ql-align-justify"></p>
  `;
};

// Helper function to safely render HTML content
const renderHtmlContent = (htmlString: string | undefined | null): React.ReactNode => {
  // If no content provided, show default template
  // Check for null, undefined, empty string, or only whitespace
  const hasNoContent =
    !htmlString ||
    htmlString === '' ||
    (typeof htmlString === 'string' && htmlString.trim() === '') ||
    (typeof htmlString === 'string' && htmlString.replace(/\s+/g, '') === '');

  if (hasNoContent) {
    const defaultTemplate = getDefaultChangeOrderDescriptionTemplate();
    return (
      <span
        dangerouslySetInnerHTML={{ __html: defaultTemplate }}
        style={{
          wordBreak: 'break-word',
          lineHeight: 1.5,
          opacity: 0.7,
          fontStyle: 'italic',
        }}
      />
    );
  }

  // Remove common HTML entities and clean up the content
  const cleanedHtml = htmlString
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

  // Check if content contains HTML tags
  const containsHtml = /<[^>]*>/g.test(cleanedHtml);

  if (containsHtml) {
    return (
      <span
        dangerouslySetInnerHTML={{ __html: cleanedHtml }}
        style={{
          wordBreak: 'break-word',
          lineHeight: 1.5,
        }}
      />
    );
  }

  // If no HTML tags, return as plain text
  return cleanedHtml;
};

interface CreateChangeOrderProps {
  estimateId?: string;
}

export const CreateChangeOrder: React.FC<CreateChangeOrderProps> = ({ estimateId }) => {
  const theme = useTheme();
  const isCreatingChangeOrder = useStore(useUnityBuildStore).isCreatingChangeOrder;
  const selectedChangeOrder = useStore(useUnityBuildStore).selectedChangeOrder;
  const selectedContract = useStore(useUnityBuildStore).selectedContract;
  const setSelectedChangeOrder = useStore(useUnityBuildStore).setSelectedChangeOrder;
  const setIsCreatingChangeOrder = useStore(useUnityBuildStore).setIsCreatingChangeOrder;
  const setIsViewingChangeOrderForm = useStore(useUnityBuildStore).setIsViewingChangeOrderForm;

  const selectedEstimateIdForChangeOrder =
    useStore(useUnityBuildStore).selectedEstimateIdForChangeOrder;
  const effectiveEstimateId: string | undefined =
    estimateId || selectedEstimateIdForChangeOrder || undefined;

  const { data: changeOrders } = useFetchChangeOrders(effectiveEstimateId);
  const { data: changeOrderStatuses } = useFetchEstimateStatuses();
  const { data: estimateList } = useFetchEstimates();

  const { mutate: createChangeOrder } = useCreateChangeOrderMutation();
  const { mutate: updateChangeOrder } = useUpdateChangeOrderMutation();

  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState<string>(formatDate(new Date().toISOString()));
  const [name, setName] = useState('');
  const [customid, setCustomId] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<{
    estimatestatusid: string;
    estimatestatusname: string;
  } | null>(null);
  const [formErrors, setFormErrors] = useState<{
    name?: string;
    customid?: string;
    status?: string;
    dueDate?: string;
    submit?: string;
  }>({});

  // Function to generate the next available custom_id for change orders
  const generateNextCustomId = useCallback(() => {
    if (!effectiveEstimateId || !changeOrders || !estimateList) return '';

    // Find the current estimate to get its custom ID
    const currentEstimate = estimateList.find(
      estimateObj => estimateObj.estimate?.estimateid === effectiveEstimateId
    )?.estimate;

    if (!currentEstimate?.customid) return '';

    const estimateCustomId = currentEstimate.customid;

    // Get all existing custom_ids for this estimate that follow the pattern customid-number
    const existingNumbers = changeOrders
      .map(co => co.changeorder?.customid)
      .filter(customId => customId && customId.startsWith(`${estimateCustomId}-`))
      .map(customId => {
        const suffix = customId?.substring(`${estimateCustomId}-`.length);
        const number = parseInt(suffix || '0', 10);
        return isNaN(number) ? 0 : number;
      })
      .sort((a, b) => a - b);

    // Find the next available number
    let nextNumber = 1;
    for (const num of existingNumbers) {
      if (num === nextNumber) {
        nextNumber++;
      } else {
        break;
      }
    }

    return `${estimateCustomId}-${nextNumber.toString().padStart(3, '0')}`;
  }, [effectiveEstimateId, changeOrders, estimateList]);

  // Form validation function
  const validateForm = () => {
    const errors: { name?: string; customid?: string; status?: string; dueDate?: string } = {};

    if (!name.trim()) {
      errors.name = 'Change Order Name is required';
    }

    if (!customid.trim()) {
      errors.customid = 'Change Order ID is required';
    }

    if (!selectedStatus?.estimatestatusid) {
      errors.status = 'Status is required';
    }

    if (!dueDate.trim()) {
      errors.dueDate = 'Due Date is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    if (!isCreatingChangeOrder) {
      const selectedChangeOrderObj = changeOrders?.find(
        contract =>
          contract.changeorder?.changeorderid.trim() === selectedChangeOrder?.changeorderid.trim()
      );

      if (!selectedChangeOrderObj) {
        setSelectedChangeOrder(null);
      } else {
        const existingCustomId = selectedChangeOrderObj.changeorder?.customid ?? '';

        // If updating an existing change order without a custom_id, generate one
        if (!existingCustomId.trim()) {
          setCustomId(generateNextCustomId());
        } else {
          setCustomId(existingCustomId);
        }

        setName(selectedChangeOrderObj.changeorder?.name ?? '');
        setDescription(selectedChangeOrderObj.changeorder?.description ?? '');
        setDueDate(formatDate(selectedChangeOrderObj.changeorder?.duedate ?? ''));
        setSelectedStatus({
          estimatestatusid: selectedChangeOrderObj.changeorder?.changeorderstatusid || '',
          estimatestatusname: selectedChangeOrderObj.changeorder?.changeorderstatusname || '',
        });
      }
    }
    // eslint-disable-next-line
  }, [selectedChangeOrder, changeOrders, estimateList, generateNextCustomId]);

  // Effect to auto-generate custom_id for new change orders
  useEffect(() => {
    if (isCreatingChangeOrder && effectiveEstimateId && changeOrders && estimateList) {
      // Only set if customid is empty (don't overwrite user input)
      if (!customid.trim()) {
        setCustomId(generateNextCustomId());
      }
    }
  }, [
    isCreatingChangeOrder,
    effectiveEstimateId,
    changeOrders,
    estimateList,
    customid,
    generateNextCustomId,
  ]);

  // Effect to set default status for new change orders
  useEffect(() => {
    if (
      isCreatingChangeOrder &&
      changeOrderStatuses &&
      changeOrderStatuses.length > 0 &&
      !selectedStatus
    ) {
      // Set "Draft" as default for new change orders
      const defaultStatus =
        changeOrderStatuses.find(
          (status: EstimateStatus.AsObject) => status.estimatestatusname === 'Draft'
        ) || changeOrderStatuses[0];
      setSelectedStatus({
        estimatestatusid: defaultStatus.estimatestatusid,
        estimatestatusname: defaultStatus.estimatestatusname,
      });
    }
  }, [isCreatingChangeOrder, changeOrderStatuses, selectedStatus]);

  type Field = 'name' | 'description' | 'duedate' | 'custumid';

  const handleSetValue = (field: Field, value: string) => {
    // Clear errors when user starts typing
    if (field === 'name' && formErrors.name) {
      setFormErrors(prev => ({ ...prev, name: undefined }));
    } else if (field === 'custumid' && (formErrors.customid || formErrors.submit)) {
      setFormErrors(prev => ({ ...prev, customid: undefined, submit: undefined }));
    } else if (field === 'duedate' && formErrors.dueDate) {
      setFormErrors(prev => ({ ...prev, dueDate: undefined }));
    }

    switch (field) {
      case 'description':
        setDescription(value);
        break;
      case 'duedate':
        setDueDate(value);
        break;
      case 'name':
        setName(value);
        break;
      case 'custumid':
        setCustomId(value);
        break;
      default:
        break;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    // Clear any previous submit errors
    setFormErrors(prev => ({ ...prev, submit: undefined, customid: undefined }));

    const formData: ChangeOrder.AsObject = {
      changeorderid: selectedChangeOrder?.changeorderid ?? '',
      description: description,
      duedate: dueDate,
      contractid: selectedContract?.contractid || '',
      datecreated: selectedChangeOrder?.datecreated || '',
      name: name,
      customid: customid,
      createdbyuserid: '', //set by the backend
      createdbyaccountid: '', //set by the backend
      lastupdated: '', //set by the backend
      estimateid: effectiveEstimateId || '',
      changeorderstatusid: selectedStatus?.estimatestatusid || '',
      changeorderstatusname: selectedStatus?.estimatestatusname || '',
    };

    try {
      if (isCreatingChangeOrder) {
        await new Promise((resolve, reject) => {
          createChangeOrder(formData, {
            onSuccess: resolve,
            onError: reject,
          });
        });
      } else {
        await new Promise((resolve, reject) => {
          updateChangeOrder(formData, {
            onSuccess: resolve,
            onError: reject,
          });
        });
      }

      // Success - close form and reset
      setIsViewingChangeOrderForm(false);
      setIsCreatingChangeOrder(false);
      resetForm();
    } catch (error: any) {
      console.error('Error saving change order:', error);

      // Check for duplicate ID constraint violation
      if (
        error?.message?.includes('unique constraint "unique_change_order_estimate_id_custom_id"')
      ) {
        setFormErrors(prev => ({
          ...prev,
          customid:
            'This Change Order ID is already in use for this estimate. Please choose a different ID.',
        }));
      } else {
        // Generic error message for other errors
        setFormErrors(prev => ({
          ...prev,
          submit: 'An error occurred while saving the change order. Please try again.',
        }));
      }
    }
  };

  const handleEstimateFormCancel = () => {
    setIsCreatingChangeOrder(false);
    setIsViewingChangeOrderForm(false);
  };

  const resetForm = () => {
    setDescription('');
    setName('');
    setCustomId('');
    setDueDate(formatDate(new Date().toISOString()));
    setSelectedStatus(null);
    setFormErrors({});
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
      <TextField
        margin="normal"
        required
        fullWidth
        id="name"
        label="Change Order Name"
        rows={4}
        value={name}
        onChange={e => handleSetValue('name', e.target.value)}
        error={!!formErrors.name}
        helperText={formErrors.name}
      />

      <FormControl fullWidth required sx={{ mt: 2 }} error={!!formErrors.status}>
        <InputLabel id="status-label">Status</InputLabel>
        <Select
          labelId="status-label"
          value={selectedStatus?.estimatestatusid || ''}
          label="Status"
          onChange={e => {
            // Clear status error when user selects a value
            if (formErrors.status) {
              setFormErrors(prev => ({ ...prev, status: undefined }));
            }

            const selected = changeOrderStatuses?.find(
              (status: EstimateStatus.AsObject) => status.estimatestatusid === e.target.value
            );
            setSelectedStatus(
              selected
                ? {
                    estimatestatusid: selected.estimatestatusid,
                    estimatestatusname: selected.estimatestatusname,
                  }
                : null
            );
          }}
        >
          <MenuItem value="" disabled>
            <em>Select Status</em>
          </MenuItem>
          {changeOrderStatuses?.map((status: EstimateStatus.AsObject) => {
            const description = getStatusMenuDescription(status.estimatestatusname);
            return (
              <MenuItem key={status.estimatestatusid} value={status.estimatestatusid}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <span>{status.estimatestatusname}</span>
                  {description && (
                    <span style={{ opacity: 0.7, fontSize: '0.85em', fontStyle: 'italic' }}>
                      {description}
                    </span>
                  )}
                </Box>
              </MenuItem>
            );
          })}
        </Select>
        {formErrors.status && (
          <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5, ml: 1.75 }}>
            {formErrors.status}
          </Box>
        )}
      </FormControl>

      {/* Change Order Description - HTML-enabled display */}
      <Box sx={{ mt: 2, mb: 1 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          gutterBottom
          sx={{ mb: 1, fontSize: '0.75rem', fontWeight: 500 }}
        >
          Change Order Description
        </Typography>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            minHeight: 100,
            backgroundColor:
              theme.palette.mode === 'dark'
                ? theme.palette.action.disabled
                : theme.palette.grey[100],
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            '& p': { margin: '8px 0' },
            '& strong': { fontWeight: 600 },
            '& em': { fontStyle: 'italic' },
            '& ul, & ol': { paddingLeft: '20px', margin: '8px 0' },
            '& li': { margin: '4px 0' },
          }}
        >
          <Typography component="div" variant="body1">
            {renderHtmlContent(description)}
          </Typography>
        </Paper>
      </Box>
      <TextField
        margin="normal"
        required
        fullWidth
        id="customId"
        label="Change Order Id"
        rows={4}
        value={customid}
        onChange={e => handleSetValue('custumid', e.target.value)}
        error={!!formErrors.customid}
        helperText={formErrors.customid}
      />
      <TextField
        margin="normal"
        required
        fullWidth
        id="dueDate"
        label="Change Order Due Date"
        type="date"
        InputLabelProps={{
          shrink: true,
        }}
        rows={4}
        value={dueDate}
        onChange={e => handleSetValue('duedate', e.target.value)}
        error={!!formErrors.dueDate}
        helperText={formErrors.dueDate}
      />

      {/* Display submit error */}
      {formErrors.submit && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {formErrors.submit}
        </Alert>
      )}

      <Button type="submit" fullWidth variant="contained" sx={{ mt: 2, mb: 2 }}>
        {isCreatingChangeOrder ? 'Create Change Order' : 'Update Change Order'}
      </Button>
      <Button onClick={handleEstimateFormCancel}>Cancel</Button>
    </Box>
  );
};
